'use client';

import React from 'react';
import { styled } from '@linaria/react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { Code, Link } from 'lucide-react';

import {
  THUMB_FOCUS_GRADIENT,
  BREAKPOINTS,
  DARK_COLORS,
} from '@/constants';
import { copyToClipboard } from '@/utils';

import Space from '@/components/Space';
import Spacer from '@/components/Spacer';
import SiteFooter from '@/components/SiteFooter';
import GeneratorHeader from '@/components/Goodies/shared/GeneratorHeader';
import GeneratorNewsletterSignup from '@/components/Goodies/shared/GeneratorNewsletterSignup';
import CodeSnippetProvider from '@/components/CodeSnippet/CodeSnippetProvider';
import OverscrollColor from '@/components/OverscrollColor';
import FadeOnChange from '@/components/FadeOnChange';

import {
  generateGradient,
  generateCodeSnippet,
  encodeUrlString,
  getRandomColorPalette,
  getRandomColorMode,
  getRandomPrecision,
  getRandomAngle,
  getRandomCurve,
  ColorWithId,
  ColorMode,
  EasingCurve,
} from './GradientGenerator.helpers';
import LogoDoodle from './LogoDoodle';
import Controls from './Controls';

interface Props {
  initialColors: Array<ColorWithId>;
  initialAngle: number;
  initialColorMode: ColorMode;
  initialEasingCurve: EasingCurve;
  initialPrecision: number;
  codeOutput: React.ReactNode;
}

function GradientGenerator({
  initialColors,
  initialColorMode,
  initialPrecision,
  initialAngle,
  initialEasingCurve,
  codeOutput,
}: Props) {
  const pathname = usePathname();

  // Colors are stored in the following format:
  //   { hue: 0-360, saturation: 0-1, lightness: 0-1 }
  // I need to keep it HSL rather than hex, because Hex doesn't have
  // context (greyscale colors like #FFF has no "embedded" hue or
  // saturation, meaning it jumps around as the user modifies colors
  // in HSL space)
  // Doing 0-1 instead of 0-100 for easy conversion with Chroma.
  const [colors, setColors] =
    React.useState<Array<ColorWithId>>(initialColors);

  const [colorMode, setColorMode] =
    React.useState<ColorMode>(initialColorMode);
  const [precision, setPrecision] =
    React.useState<number>(initialPrecision);
  const [angle, setAngle] = React.useState<number>(initialAngle);
  const [easingCurve, setEasingCurve] = React.useState<EasingCurve>(
    initialEasingCurve
  );

  const [copyCodeStatus, setCopyCodeStatus] = React.useState('idle');
  const [copyUrlStatus, setCopyUrlStatus] = React.useState('idle');

  React.useEffect(() => {
    let timeoutId: Number;
    if (copyCodeStatus === 'success') {
      timeoutId = window.setTimeout(() => {
        setCopyCodeStatus('idle');
      }, 2250);
    }

    return () => {
      if (typeof timeoutId === 'number') {
        window.clearTimeout(timeoutId);
      }
    };
  }, [copyCodeStatus]);

  React.useEffect(() => {
    let timeoutId: Number;
    if (copyUrlStatus === 'success') {
      timeoutId = window.setTimeout(() => {
        setCopyUrlStatus('idle');
      }, 2250);
    }

    return () => {
      if (typeof timeoutId === 'number') {
        window.clearTimeout(timeoutId);
      }
    };
  }, [copyUrlStatus]);

  const gradientString = React.useMemo(() => {
    return generateGradient({
      colors,
      angle,
      colorMode,
      easingCurve,
      precision,
    });
  }, [colors, angle, colorMode, easingCurve, precision]);
  const deferredGradientString =
    React.useDeferredValue(gradientString);

  const shuffle = React.useCallback(() => {
    setColors(getRandomColorPalette());
    setColorMode(getRandomColorMode());
    setPrecision(getRandomPrecision());
    setEasingCurve(getRandomCurve());
    setAngle(getRandomAngle());
  }, []);

  const generateUrlForClipboard = React.useCallback(() => {
    const urlString = encodeUrlString({
      colors,
      angle,
      colorMode,
      precision,
      easingCurve,
    });

    return `${window.location.origin}${pathname}?${urlString}`;
  }, [colors, angle, colorMode, precision, easingCurve, pathname]);

  const code = generateCodeSnippet(deferredGradientString);

  function handleCopyCode() {
    copyToClipboard(code).then(() => {
      setCopyCodeStatus('success');
    });
  }
  function handleCopyUrl() {
    const url = generateUrlForClipboard();

    copyToClipboard(url).then(() => {
      setCopyUrlStatus('success');
    });
  }

  return (
    <Wrapper>
      <GeneratorHeader
        maxWidth={1200}
        blogPostHref="/css/make-beautiful-gradients/"
      />

      <SlopesWrapper>
        <Slopes
          width={2400}
          height={1080}
          alt=""
          src="/images/shadow-palette/slopes.png"
        />
      </SlopesWrapper>

      <Spacer size={32} />

      <InnerWrapper>
        <DemoWrapper>
          <Demo
            style={{
              '--gradient': gradientString,
            }}
          />
        </DemoWrapper>
        <Header>
          <LogoDoodle onClick={shuffle} />
          <h1>Gradient Generator</h1>
          <Subtitle>Beautiful, lush gradients ✨</Subtitle>
        </Header>

        <ControlsWrapper>
          <Controls
            colors={colors}
            colorMode={colorMode}
            precision={precision}
            angle={angle}
            easingCurve={easingCurve}
            setColors={setColors}
            setColorMode={setColorMode}
            setPrecision={setPrecision}
            setAngle={setAngle}
            setEasingCurve={setEasingCurve}
          />
        </ControlsWrapper>

        <OutputWrapper>
          <OutputHeader>
            <OutputLabel>Your Gradient:</OutputLabel>
          </OutputHeader>
          <CodeWrapper>
            <CodeSnippetProvider lang="css" code={code}>
              {codeOutput}
            </CodeSnippetProvider>
          </CodeWrapper>
          <Actions>
            <ActionButton
              onClick={handleCopyUrl}
              disabled={copyUrlStatus === 'success'}
            >
              <Link size={20} />
              <TextSpan>
                <FadeOnChange changeKey={copyUrlStatus}>
                  {copyUrlStatus === 'success'
                    ? 'Copied!'
                    : 'Copy URL'}
                </FadeOnChange>
              </TextSpan>
            </ActionButton>

            <ActionButton
              onClick={handleCopyCode}
              disabled={copyCodeStatus === 'success'}
            >
              <Code size={20} />
              <TextSpan>
                <FadeOnChange changeKey={copyCodeStatus}>
                  {copyCodeStatus === 'success'
                    ? 'Copied!'
                    : 'Copy CSS'}
                </FadeOnChange>
              </TextSpan>
            </ActionButton>
          </Actions>
        </OutputWrapper>
      </InnerWrapper>

      <GeneratorNewsletterSignup />

      <FooterWrapper>
        <SiteFooter />
      </FooterWrapper>

      <OverscrollColor color={DARK_COLORS['--color-background']} />
    </Wrapper>
  );
}

const Wrapper = styled(Space)`
  position: relative;
  min-height: 100vh;
  isolation: isolate;
  /* IMPORTANT: This stops the page scroll position from jumping around wildly when the controls are tweaked, causing the height of the code snippet to get bigger/smaller. */
  overflow-anchor: none !important;

  --background-values: 210deg 30% 8%;
  --color-background: hsl(var(--background-values)) !important;
  --shadow-color: 0deg 0% 0%;
  /* prettier-ignore */
  --shadow-elevation-very-low:
    0px 0.4px 0.4px hsl(var(--shadow-color) / 0.09),
    0px 0.6px 0.6px -1.6px hsl(var(--shadow-color) / 0.08),
    0px 1.3px 1.3px -3.2px hsl(var(--shadow-color) / 0.07);
  /* prettier-ignore */
  --shadow-elevation-low:
    0px 0.4px 0.4px hsl(var(--shadow-color) / 0.1),
    0px 1px 1px -1.1px hsl(var(--shadow-color) / 0.09),
    0.1px 2.6px 2.7px -2.1px hsl(var(--shadow-color) / 0.08),
    0.2px 6.6px 6.7px -3.2px hsl(var(--shadow-color) / 0.07);
  /* prettier-ignore */
  --shadow-elevation-medium:
    0px 0.4px 0.4px hsl(var(--shadow-color) / 0.125),
    0px 1.4px 1.4px -0.5px hsl(var(--shadow-color) / 0.11),
    0.1px 2.5px 2.6px -0.9px hsl(var(--shadow-color) / 0.10),
    0.1px 4.3px 4.4px -1.4px hsl(var(--shadow-color) / 0.09),
    0.2px 7.2px 7.3px -1.8px hsl(var(--shadow-color) / 0.08);

  @media ${BREAKPOINTS.mdAndSmaller} {
    --background-values: 0deg 0% 0%;
  }

  && {
    border-radius: 0px;
  }
`;

const InnerWrapper = styled.div`
  display: grid;
  grid-gap: 64px 48px;
  grid-template-columns: 1fr minmax(350px, 1fr);
  grid-template-areas:
    'demo header'
    'demo controls'
    'demo code';
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px;
  padding-bottom: 128px;
  isolation: isolate;

  @media ${BREAKPOINTS.mdAndSmaller} {
    grid-template-columns: 1fr;
    grid-template-areas:
      'header'
      'demo'
      'controls'
      'code';
    padding: 16px;
    padding-bottom: 128px;
  }
`;

const DemoWrapper = styled.div`
  grid-area: demo;
  position: sticky;
  z-index: 3;
  top: 32px;
  align-self: start;
  display: flex;
  justify-content: center;
  height: 100%;
  max-height: min(860px, calc(100vh - 32px * 2));
  padding: 8px;
  background-color: hsl(var(--background-values));
  filter: drop-shadow(0px 0px 16px var(--color-background));
  border: 1px solid var(--color-content-outline);
  border-radius: 16px;

  @media ${BREAKPOINTS.mdAndSmaller} {
    z-index: 10;
    top: 48px;
    padding: 0px;
    height: min(250px, 40vh);
    border-radius: 16px;
    box-shadow:
      0px 0px 2px hsl(var(--background-values) / 0.2),
      0px 0px 4px hsl(var(--background-values) / 0.2),
      0px 0px 8px hsl(var(--background-values) / 0.2),
      0px 0px 16px hsl(var(--background-values) / 0.2),
      0px 0px 32px hsl(var(--background-values) / 0.2),
      0px 0px 64px hsl(var(--background-values) / 0.3),
      0px 0px 96px hsl(var(--background-values) / 0.3);
  }
`;

const Demo = styled.div`
  flex: 1;
  background: var(--gradient);
  width: 100%;
  border-radius: 8px;
`;

const Header = styled.header`
  position: relative;
  z-index: 3;
  grid-area: header;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-top: 32px;
  isolation: isolate;

  h1 {
    position: relative;
    z-index: 2;
    font-size: 2rem;
    font-weight: var(--font-weight-bold);
    line-height: 1.2;
    color: initial;
    background-color: #ffb23e;
    background-image: ${THUMB_FOCUS_GRADIENT};
    background-size: 100%;
    background-clip: text;
    -webkit-background-clip: text;
    -moz-background-clip: text;
    -webkit-text-fill-color: transparent;
    -moz-text-fill-color: transparent;
    /* margin-top: -32px; */
    margin-top: 32px;
    /* prettier-ignore */
    filter:
      drop-shadow(0px 0px 2px hsl(var(--background-values) / 0.5))
      drop-shadow(0px 0px 4px hsl(var(--background-values) / 0.5))
      drop-shadow(0px 0px 8px hsl(var(--background-values) / 0.5))
      drop-shadow(0px 0px 16px hsl(var(--background-values) / 0.5))
      drop-shadow(0px 0px 32px hsl(var(--background-values) / 0.5))
      drop-shadow(0px 0px 64px hsl(var(--background-values) / 0.5));
  }
`;

const Subtitle = styled.p`
  position: relative;
  z-index: 3;
  font-size: 1.25rem;
  text-align: center;
  max-width: 400px;
  margin: 8px auto 0;
  color: var(--color-gray-700);
`;

const ControlsWrapper = styled.div`
  grid-area: controls;
  position: relative;
  z-index: 4;
`;

const SlopesWrapper = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 60px;
  width: 100%;
  opacity: 0.75;
  filter: blur(5px);

  @media ${BREAKPOINTS.mdAndSmaller} {
    /* The image needs to be pushed up further so that the email input has high-enough contrast, and isn't over a purple mountain. */
    bottom: 256px;

    /*
      On mobile, the background becomes solid black, and there's an awkward edge behind the footer, between the bottom of the slopes image and the page background. Add a gradient to smooth this out.
    */
    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 200px;
      background: linear-gradient(
        to bottom,
        transparent,
        var(--color-background)
      );
    }
  }
`;

const Slopes = styled(Image)`
  object-fit: cover;
  object-position: top center;
`;

const OutputWrapper = styled.div`
  --scrollbar-bg: var(--syntax-bg);
  --scrollbar-thumb: var(--color-gray-300);
  grid-area: code;
  position: relative;
  z-index: 1;
  min-width: 0px;
`;
const OutputHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
  user-select: none;
`;
const OutputLabel = styled.div`
  font-weight: var(--font-weight-medium);
`;
const CodeWrapper = styled.div`
  background: var(--color-background);
  outline: 1px solid var(--color-content-outline);
  border-radius: 8px;
`;

const FooterWrapper = styled.div`
  padding-bottom: 32px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  transform: translateY(calc(100% + 16px));
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 8px 12px;
  font-size: 0.875rem;
  border: 1px solid var(--color-gray-300);
  border-radius: 4px;
  color: white;

  &:not(:disabled):hover {
    border-color: var(--color-gray-300);
  }

  &:focus {
    outline-offset: 4px;
    outline-color: white;
  }

  &:disabled {
    opacity: 1;
    color: white;
    cursor: default;
  }

  svg {
    display: block;
  }
`;

const TextSpan = styled.span`
  display: block;
  min-width: 4.25rem;
`;

export default GradientGenerator;
